// src/api/modelService.js
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_BASE_URL;

/**
 * Upload a new ML model with metadata and optional files.
 * @param {FormData} formData - must include: title, category, framework, input_type
 *   Optional: description, output_type, accuracy, metrics (JSON string), layers_info,
 *   model_size_mb, training_time_sec, num_classes, num_samples, tags (comma-separated),
 *   demo_instructions, sample_input_description, inference_config (JSON string),
 *   model_file (File), thumbnail (File)
 * @param {Function} onUploadProgress - optional callback receiving a 0-100 percentage
 */
export const uploadModel = async (formData, token, onUploadProgress) => {
  const response = await axios.post(`${API_URL}/metadata/models/upload`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
      'Authorization': `Bearer ${token}`,
    },
    onUploadProgress: onUploadProgress
      ? (event) => event.total && onUploadProgress(Math.round((event.loaded * 100) / event.total))
      : undefined,
  });
  return response.data;
};

/**
 * List models with optional search/filter/pagination.
 */
export const getModels = async (params = {}) => {
  const response = await axios.get(`${API_URL}/metadata/models`, { params });
  return response.data;
};

/**
 * Get a single model by ID.
 */
export const getModel = async (id) => {
  const response = await axios.get(`${API_URL}/metadata/models/${id}`);
  return response.data;
};

/**
 * Update model metadata.
 */
export const updateModel = async (id, formData, token) => {
  const response = await axios.put(`${API_URL}/metadata/models/${id}`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
      'Authorization': `Bearer ${token}`,
    },
  });
  return response.data;
};

/**
 * Delete a model.
 */
export const deleteModel = async (id, token) => {
  const response = await axios.delete(`${API_URL}/metadata/models/${id}`, {
    headers: { 'Authorization': `Bearer ${token}` },
  });
  return response.data;
};

/**
 * Approve or reject a model (admin only).
 * @param {'approved'|'rejected'} decision
 */
export const reviewModel = async (id, decision, note, token) => {
  const response = await axios.patch(`${API_URL}/metadata/models/${id}/review`, { decision, note }, {
    headers: { 'Authorization': `Bearer ${token}` },
  });
  return response.data;
};

/**
 * Download a model file. Fetched as a blob so the login token is sent
 * (needed for the uploader/admin to download a model that is still pending).
 */
export const downloadModel = async (id, fallbackName = 'model') => {
  const response = await axios.get(`${API_URL}/metadata/models/${id}/download`, {
    responseType: 'blob',
  });

  const disposition = response.headers['content-disposition'] || '';
  const match = /filename="?([^";]+)"?/.exec(disposition);
  const filename = match ? match[1] : fallbackName;

  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

/**
 * Get model thumbnail URL.
 */
export const getModelThumbnailUrl = (id) => {
  return `${API_URL}/metadata/models/${id}/thumbnail`;
};

/**
 * Run inference demo on a model with an input file.
 */
export const runModelDemo = async (id, inputFile) => {
  const formData = new FormData();
  formData.append('input_file', inputFile);

  const response = await axios.post(`${API_URL}/metadata/models/${id}/demo`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};
